// src/pages/BookkeepingReportsPage.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ProfitLoss, BalanceSheet, TrialBalance, AccountReport } from '../components/AccountReports'; // [marker: account-reports v1]
import { OpenInvoicesReport, SalesReport, VendorExpenseReport, FiscalYearSetting } from '../components/FinReports'; // [marker: fin-reports v1]
import { ProfitLossByClass, FunctionalExpenses, EventProfitLoss, AuditTrail } from '../components/ClassReports'; // [marker: books-pro v1]
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';

const fmt = (n) => `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const COLORS = ['#1a3a5c','#2a4f7c','#c8a84b','#16a34a','#dc2626','#d97706','#6b7280','#2563eb'];

export default function BookkeepingReportsPage() {
  // [marker: account-reports v1] ?account=ID opens that account's report
  const qs = new URLSearchParams(window.location.search);
  const [activeTab, setActiveTab] = useState(qs.get('account') ? 'account' : 'pl');
  const [drill, setDrill] = useState(qs.get('account') ? { accountId: parseInt(qs.get('account'), 10), range: null } : null);
  const year = new Date().getFullYear();
  const openAccount = (acct, range) => { setDrill({ accountId: acct.id, range }); setActiveTab('account'); };
  const { isManagerOrAbove } = useAuth();
  const [fyKey, setFyKey] = useState(0); // remount the reports when the fiscal year changes

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Financial Reports</h1>
        <FiscalYearSetting canEdit={isManagerOrAbove()} onChanged={() => setFyKey(k => k + 1)} />
      </div>

      <div className="tabs">
        {[
          { key: 'pl',       label: 'Profit & Loss' },
          { key: 'balance',  label: 'Balance Sheet' },
          { key: 'tb',       label: 'Trial Balance' },
          { key: 'account',  label: 'Account Report' },
          { key: 'ledger',   label: 'General Ledger' },
          { key: 'open',     label: 'Open Invoices' },
          { key: 'sales',    label: 'Sales by Item' },
          { key: 'vendors',  label: 'Expenses by Vendor' },
          { key: 'eventpl',  label: 'Event P&L' },
          { key: 'byclass',  label: 'P&L by Class' },
          { key: 'functional', label: 'Functional Expenses (990)' },
          ...(isManagerOrAbove() ? [{ key: 'audit', label: 'Audit Trail' }] : []),
        ].map(tab => (
          <button key={tab.key} className={`tab-btn${activeTab === tab.key ? ' active' : ''}`} onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      <div key={fyKey}>
      {activeTab === 'pl'      && <ProfitLoss onDrill={openAccount} />}
      {activeTab === 'balance' && <BalanceSheet onDrill={openAccount} />}
      {activeTab === 'tb'      && <TrialBalance onDrill={openAccount} />}
      {activeTab === 'account' && <AccountReport initialAccountId={drill?.accountId} initialRange={drill?.range} />}
      {activeTab === 'ledger'  && <GeneralLedgerReport />}
      {activeTab === 'open'    && <OpenInvoicesReport />}
      {activeTab === 'sales'   && <SalesReport />}
      {activeTab === 'vendors' && <VendorExpenseReport />}
      {activeTab === 'eventpl' && <EventProfitLoss />}
      {activeTab === 'byclass' && <ProfitLossByClass />}
      {activeTab === 'functional' && <FunctionalExpenses />}
      {activeTab === 'audit' && <AuditTrail />}
      </div>
    </div>
  );
}

// ─── Profit & Loss ────────────────────────────────────────────────────────────
function PLReport() {
  const year = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${year}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bookkeeping-reports/pl?startDate=${startDate}&endDate=${endDate}`);
      setData(res.data);
    } catch { toast.error('Failed to load P&L.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Group income by parent
  const groupByParent = (rows) => {
    const groups = {};
    rows.forEach(r => {
      const key = r.parent_account_id ? r.parent_name || 'Other' : r.name;
      if (!groups[key]) groups[key] = { name: key, items: [], total: 0 };
      groups[key].items.push(r);
      groups[key].total += parseFloat(r.period_amount);
    });
    return Object.values(groups).filter(g => g.total !== 0).sort((a,b) => b.total - a.total);
  };

  return (
    <div>
      {/* Date range controls */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)' }}>From</label>
            <input className="form-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: 150 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)' }}>To</label>
            <input className="form-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: 150 }} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={load}>Run Report</button>
          {data && (
            <button className="btn btn-secondary btn-sm" onClick={() => window.print()}>🖨 Print</button>
          )}
          {/* Quick range buttons */}
          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            {[
              { label: 'This Year', s: `${new Date().getFullYear()}-01-01`, e: new Date().toISOString().split('T')[0] },
              { label: 'Last Year', s: `${new Date().getFullYear()-1}-01-01`, e: `${new Date().getFullYear()-1}-12-31` },
              { label: 'This Month', s: `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-01`, e: new Date().toISOString().split('T')[0] },
            ].map(r => (
              <button key={r.label} className="btn btn-ghost btn-sm" onClick={() => { setStartDate(r.s); setEndDate(r.e); }}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div>}

      {!loading && data && (
        <>
          {/* Summary stats */}
          <div className="stat-grid" style={{ marginBottom: 24 }}>
            <div className="stat-card">
              <div className="stat-label">Total Income</div>
              <div className="stat-value" style={{ color: 'var(--success)' }}>{fmt(data.totalIncome)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Expenses</div>
              <div className="stat-value" style={{ color: 'var(--danger)' }}>{fmt(data.totalExpense)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Net Income</div>
              <div className="stat-value" style={{ color: data.netIncome >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {fmt(data.netIncome)}
              </div>
              <div className="stat-sub">{fmtDate(data.startDate)} – {fmtDate(data.endDate)}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            {/* Income chart */}
            <div className="card">
              <div className="card-header"><span className="card-title">Income by Category</span></div>
              <div style={{ padding: '12px 16px', height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={groupByParent(data.income).slice(0,8)} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name.slice(0,15)} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                      {groupByParent(data.income).slice(0,8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Expense chart */}
            <div className="card">
              <div className="card-header"><span className="card-title">Expenses by Category</span></div>
              <div style={{ padding: '12px 16px', height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={groupByParent(data.expense).slice(0,8)} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                    <Tooltip formatter={v => fmt(v)} />
                    <Bar dataKey="total" fill="#dc2626" radius={[0,4,4,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Detailed tables */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <PLSection title="Income" rows={data.income} total={data.totalIncome} color="var(--success)" />
            <PLSection title="Expenses" rows={data.expense} total={data.totalExpense} color="var(--danger)" />
          </div>

          {/* Net income footer */}
          <div className="card" style={{ marginTop: 16, padding: '16px 20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>Net Income</span>
              <span style={{ fontWeight: 700, fontSize: '1.25rem', color: data.netIncome >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {fmt(data.netIncome)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PLSection({ title, rows, total, color }) {
  const [expanded, setExpanded] = useState({});
  const groups = {};
  rows.forEach(r => {
    const key = r.parent_account_id ? (r.parent_name || 'Other') : r.name;
    if (!groups[key]) groups[key] = { name: key, items: [], total: 0 };
    groups[key].items.push(r);
    groups[key].total += parseFloat(r.period_amount);
  });

  const sortedGroups = Object.values(groups).filter(g => g.total !== 0).sort((a,b) => b.total - a.total);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{title}</span>
        <span style={{ fontWeight: 700, color }}>{fmt(total)}</span>
      </div>
      <div>
        {sortedGroups.map(g => (
          <div key={g.name}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 20px', cursor: g.items.length > 1 ? 'pointer' : 'default', borderBottom: '1px solid var(--gray-100)', background: 'var(--gray-50)' }}
              onClick={() => g.items.length > 1 && setExpanded(e => ({...e, [g.name]: !e[g.name]}))}
            >
              <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                {g.items.length > 1 && <span style={{ marginRight: 6, color: 'var(--gray-400)', fontSize: '0.7rem' }}>{expanded[g.name] ? '▼' : '▶'}</span>}
                {g.name}
              </span>
              <span style={{ fontWeight: 600, fontSize: '0.875rem', color }}>{fmt(g.total)}</span>
            </div>
            {expanded[g.name] && g.items.map(item => (
              parseFloat(item.period_amount) !== 0 && (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 20px 7px 36px', borderBottom: '1px solid var(--gray-100)', fontSize: '0.8rem' }}>
                  <span style={{ color: 'var(--gray-600)' }}>{item.name}</span>
                  <span>{fmt(item.period_amount)}</span>
                </div>
              )
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Balance Sheet ────────────────────────────────────────────────────────────
function BalanceSheetReport() {
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bookkeeping-reports/balance-sheet?asOfDate=${asOfDate}`);
      setData(res.data);
    } catch { toast.error('Failed to load balance sheet.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const getTotal = (types) => {
    if (!data) return 0;
    return types.reduce((sum, type) => {
      return sum + (data.accounts[type] || []).reduce((s,a) => s + parseFloat(a.balance), 0);
    }, 0);
  };

  const totalAssets = getTotal(['bank','accounts_receivable','asset']);
  const totalLiabilities = getTotal(['accounts_payable','credit_card','loan','other_liability']);
  const totalEquity = getTotal(['equity']);

  return (
    <div>
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)' }}>As of</label>
          <input className="form-input" type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} style={{ width: 160 }} />
          <button className="btn btn-primary btn-sm" onClick={load}>Run Report</button>
        </div>
      </div>

      {loading && <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div>}

      {!loading && data && (
        <>
          <div className="stat-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card"><div className="stat-label">Total Assets</div><div className="stat-value" style={{ color: 'var(--navy)' }}>{fmt(totalAssets)}</div></div>
            <div className="stat-card"><div className="stat-label">Total Liabilities</div><div className="stat-value" style={{ color: 'var(--danger)' }}>{fmt(totalLiabilities)}</div></div>
            <div className="stat-card"><div className="stat-label">Total Equity</div><div className="stat-value" style={{ color: 'var(--success)' }}>{fmt(totalEquity)}</div></div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <BSSection title="Assets" types={['bank','accounts_receivable','asset']} accounts={data.accounts} />
            </div>
            <div>
              <BSSection title="Liabilities" types={['accounts_payable','credit_card','loan','other_liability']} accounts={data.accounts} />
              <BSSection title="Equity" types={['equity']} accounts={data.accounts} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BSSection({ title, types, accounts }) {
  const rows = types.flatMap(t => accounts[t] || []).filter(a => parseFloat(a.balance) !== 0);
  const total = rows.reduce((s,a) => s + parseFloat(a.balance), 0);
  if (!rows.length) return null;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header"><span className="card-title">{title}</span><span style={{ fontWeight: 700 }}>{fmt(total)}</span></div>
      <div>
        {rows.map(a => (
          <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 20px', borderBottom: '1px solid var(--gray-100)', fontSize: '0.875rem' }}>
            <span style={{ color: 'var(--gray-700)' }}>{a.name}</span>
            <span style={{ fontWeight: 500 }}>{fmt(a.balance)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── General Ledger ───────────────────────────────────────────────────────────
function GeneralLedgerReport() {
  const year = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${year}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`/bookkeeping-reports/general-ledger?startDate=${startDate}&endDate=${endDate}&page=${p}&limit=100`);
      setData(res.data.data || []);
      setPagination(res.data.pagination || {});
      setPage(p);
    } catch { toast.error('Failed to load ledger.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)' }}>From</label>
            <input className="form-input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: 150 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)' }}>To</label>
            <input className="form-input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: 150 }} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => load(1)}>Run</button>
          <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginLeft: 'auto' }}>{pagination.total || 0} entries</span>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Date</th><th>Entry #</th><th>Account</th><th>Description</th><th>Payee</th><th style={{ textAlign: 'right' }}>Debit</th><th style={{ textAlign: 'right' }}>Credit</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></td></tr>
            : data.length === 0 ? <tr><td colSpan={7}><div className="empty-state" style={{ padding: 24 }}><p>No entries found</p></div></td></tr>
            : data.map((row, i) => (
              <tr key={i}>
                <td style={{ fontSize: '0.8rem' }}>{fmtDate(row.entry_date)}</td>
                <td style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'var(--gray-400)' }}>{row.entry_number}</td>
                <td style={{ fontSize: '0.8rem' }}>{row.account_name}</td>
                <td style={{ fontSize: '0.8rem', color: 'var(--gray-600)' }}>{row.line_description || row.entry_description}</td>
                <td style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{row.payee || '—'}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem', color: 'var(--success)' }}>{parseFloat(row.debit) > 0 ? fmt(row.debit) : ''}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem', color: 'var(--danger)' }}>{parseFloat(row.credit) > 0 ? fmt(row.credit) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => load(page - 1)}>← Prev</button>
          <span style={{ padding: '5px 12px', fontSize: '0.875rem', color: 'var(--gray-500)' }}>Page {page} of {pagination.totalPages}</span>
          <button className="btn btn-secondary btn-sm" disabled={page >= pagination.totalPages} onClick={() => load(page + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ─── AR Aging ─────────────────────────────────────────────────────────────────
function ARAgingReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/bookkeeping-reports/ar-aging')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load AR aging.'))
      .finally(() => setLoading(false));
  }, []);

  const totals = data.reduce((acc, row) => ({
    current:   acc.current   + parseFloat(row.current_amount),
    days1_30:  acc.days1_30  + parseFloat(row.days_1_30),
    days31_60: acc.days31_60 + parseFloat(row.days_31_60),
    days61_90: acc.days61_90 + parseFloat(row.days_61_90),
    over90:    acc.over90    + parseFloat(row.over_90),
    total:     acc.total     + parseFloat(row.total_outstanding),
  }), { current: 0, days1_30: 0, days31_60: 0, days61_90: 0, over90: 0, total: 0 });

  return (
    <div>
      <div className="stat-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Current</div><div className="stat-value" style={{ fontSize: '1.4rem' }}>{fmt(totals.current)}</div></div>
        <div className="stat-card"><div className="stat-label">1–30 Days</div><div className="stat-value" style={{ fontSize: '1.4rem', color: totals.days1_30 > 0 ? 'var(--warning)' : undefined }}>{fmt(totals.days1_30)}</div></div>
        <div className="stat-card"><div className="stat-label">31–60 Days</div><div className="stat-value" style={{ fontSize: '1.4rem', color: totals.days31_60 > 0 ? 'var(--warning)' : undefined }}>{fmt(totals.days31_60)}</div></div>
        <div className="stat-card"><div className="stat-label">61–90 Days</div><div className="stat-value" style={{ fontSize: '1.4rem', color: totals.days61_90 > 0 ? 'var(--danger)' : undefined }}>{fmt(totals.days61_90)}</div></div>
        <div className="stat-card"><div className="stat-label">Over 90 Days</div><div className="stat-value" style={{ fontSize: '1.4rem', color: totals.over90 > 0 ? 'var(--danger)' : undefined }}>{fmt(totals.over90)}</div></div>
        <div className="stat-card"><div className="stat-label">Total Outstanding</div><div className="stat-value" style={{ color: 'var(--navy)' }}>{fmt(totals.total)}</div></div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Business</th>
              <th style={{ textAlign: 'right' }}>Current</th>
              <th style={{ textAlign: 'right' }}>1–30 Days</th>
              <th style={{ textAlign: 'right' }}>31–60 Days</th>
              <th style={{ textAlign: 'right' }}>61–90 Days</th>
              <th style={{ textAlign: 'right' }}>Over 90</th>
              <th style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></td></tr>
            : data.length === 0 ? <tr><td colSpan={7}><div className="empty-state" style={{ padding: 24 }}><div className="empty-state-icon">✅</div><h3>No outstanding receivables</h3></div></td></tr>
            : data.map((row, i) => (
              <tr key={i}>
                <td className="td-primary">{row.business_name}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem' }}>{parseFloat(row.current_amount) > 0 ? fmt(row.current_amount) : '—'}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem', color: parseFloat(row.days_1_30) > 0 ? 'var(--warning)' : undefined }}>{parseFloat(row.days_1_30) > 0 ? fmt(row.days_1_30) : '—'}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem', color: parseFloat(row.days_31_60) > 0 ? 'var(--warning)' : undefined }}>{parseFloat(row.days_31_60) > 0 ? fmt(row.days_31_60) : '—'}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem', color: parseFloat(row.days_61_90) > 0 ? 'var(--danger)' : undefined }}>{parseFloat(row.days_61_90) > 0 ? fmt(row.days_61_90) : '—'}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem', color: parseFloat(row.over_90) > 0 ? 'var(--danger)' : undefined, fontWeight: parseFloat(row.over_90) > 0 ? 700 : 400 }}>{parseFloat(row.over_90) > 0 ? fmt(row.over_90) : '—'}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(row.total_outstanding)}</td>
              </tr>
            ))}
            {data.length > 0 && (
              <tr style={{ background: 'var(--gray-50)', fontWeight: 700 }}>
                <td>TOTAL</td>
                <td style={{ textAlign: 'right' }}>{fmt(totals.current)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totals.days1_30)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totals.days31_60)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totals.days61_90)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totals.over90)}</td>
                <td style={{ textAlign: 'right' }}>{fmt(totals.total)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── AP Aging ─────────────────────────────────────────────────────────────────
function APAgingReport() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/bookkeeping-reports/ap-aging')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load AP aging.'))
      .finally(() => setLoading(false));
  }, []);

  const totals = data.reduce((acc,row) => ({
    current: acc.current + parseFloat(row.current_amount),
    over30:  acc.over30  + parseFloat(row.over_30),
    total:   acc.total   + parseFloat(row.total_owed),
  }), { current: 0, over30: 0, total: 0 });

  return (
    <div>
      <div className="stat-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Current</div><div className="stat-value">{fmt(totals.current)}</div></div>
        <div className="stat-card"><div className="stat-label">Over 30 Days</div><div className="stat-value" style={{ color: totals.over30 > 0 ? 'var(--danger)' : undefined }}>{fmt(totals.over30)}</div></div>
        <div className="stat-card"><div className="stat-label">Total Owed to Vendors</div><div className="stat-value" style={{ color: 'var(--navy)' }}>{fmt(totals.total)}</div></div>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Vendor</th><th style={{ textAlign: 'right' }}>Current</th><th style={{ textAlign: 'right' }}>Over 30 Days</th><th style={{ textAlign: 'right' }}>Total Owed</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={4} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></td></tr>
            : data.length === 0 ? <tr><td colSpan={4}><div className="empty-state" style={{ padding: 24 }}><div className="empty-state-icon">✅</div><h3>No outstanding payables</h3></div></td></tr>
            : data.map((row, i) => (
              <tr key={i}>
                <td className="td-primary">{row.vendor_name}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem' }}>{parseFloat(row.current_amount) > 0 ? fmt(row.current_amount) : '—'}</td>
                <td style={{ textAlign: 'right', fontSize: '0.875rem', color: parseFloat(row.over_30) > 0 ? 'var(--danger)' : undefined, fontWeight: parseFloat(row.over_30) > 0 ? 700 : 400 }}>{parseFloat(row.over_30) > 0 ? fmt(row.over_30) : '—'}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmt(row.total_owed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Event P&L ────────────────────────────────────────────────────────────────
function EventPLReport() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/events?limit=100&upcoming_only=false')
      .then(r => setEvents(r.data.data || []))
      .catch(console.error);
  }, []);

  const load = async () => {
    if (!selectedEventId) return toast.error('Select an event.');
    setLoading(true);
    try {
      const res = await api.get(`/bookkeeping-reports/event-pl/${selectedEventId}`);
      setData(res.data);
    } catch { toast.error('Failed to load event P&L.'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select className="form-select" style={{ minWidth: 280 }} value={selectedEventId} onChange={e => setSelectedEventId(e.target.value)}>
            <option value="">Select an event...</option>
            {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name} — {new Date(ev.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</option>)}
          </select>
          <button className="btn btn-primary btn-sm" onClick={load}>Run Report</button>
        </div>
      </div>

      {loading && <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div>}

      {!loading && data && (
        <>
          <div className="stat-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card"><div className="stat-label">Ticket Revenue</div><div className="stat-value" style={{ color: 'var(--success)' }}>{fmt(data.ticketRevenue)}</div></div>
            <div className="stat-card"><div className="stat-label">Sponsor Revenue</div><div className="stat-value" style={{ color: 'var(--success)' }}>{fmt(data.sponsorRevenue)}</div></div>
            <div className="stat-card"><div className="stat-label">Total Revenue</div><div className="stat-value" style={{ color: 'var(--success)' }}>{fmt(data.totalRevenue)}</div></div>
            <div className="stat-card"><div className="stat-label">Total Expenses</div><div className="stat-value" style={{ color: 'var(--danger)' }}>{fmt(data.totalExpenses)}</div></div>
            <div className="stat-card">
              <div className="stat-label">Net Income</div>
              <div className="stat-value" style={{ color: data.netIncome >= 0 ? 'var(--success)' : 'var(--danger)' }}>{fmt(data.netIncome)}</div>
            </div>
          </div>

          {data.expenses.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Event Expenses</span></div>
              <div className="table-wrap" style={{ border: 'none' }}>
                <table>
                  <thead><tr><th>Date</th><th>Vendor</th><th>Account</th><th>Method</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                  <tbody>
                    {data.expenses.map((e, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: '0.8rem' }}>{fmtDate(e.expense_date)}</td>
                        <td style={{ fontSize: '0.875rem' }}>{e.vendor_name || '—'}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{e.account_name}</td>
                        <td style={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>{e.payment_method}</td>
                        <td style={{ textAlign: 'right', color: 'var(--danger)', fontWeight: 500 }}>{fmt(e.amount)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: 'var(--gray-50)', fontWeight: 700 }}>
                      <td colSpan={4}>Total Expenses</td>
                      <td style={{ textAlign: 'right', color: 'var(--danger)' }}>{fmt(data.totalExpenses)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
